import { ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import type { InquiryStatus } from '@/types/api';

type BadgeTone = 'neutral' | 'primary' | 'accent' | 'warning' | 'danger' | 'success' | 'info';
const badgeTones: Record<BadgeTone, string> = {
  neutral: 'bg-surface-2 text-ink-soft border-line',
  primary: 'bg-primary/10 text-primary border-primary/20',
  accent: 'bg-accent-soft text-accent-dark border-emerald-100',
  warning: 'bg-amber-50 text-amber-800 border-amber-200',
  danger: 'bg-red-50 text-red-700 border-red-200',
  success: 'bg-emerald-50 text-emerald-800 border-emerald-200',
  info: 'bg-sky-50 text-sky-800 border-sky-200',
};

export function Badge({ tone = 'neutral', children, className }: { tone?: BadgeTone; children: ReactNode; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold whitespace-nowrap', badgeTones[tone], className)}>
      {children}
    </span>
  );
}

export const STATUS_LABEL: Record<InquiryStatus, string> = {
  NEW: 'New',
  CONTACTED: 'Contacted',
  IN_PROGRESS: 'In progress',
  QUOTED: 'Quoted',
  WON: 'Won',
  LOST: 'Lost',
  ARCHIVED: 'Archived',
};

const STATUS_TONE: Record<InquiryStatus, BadgeTone> = {
  NEW: 'info',
  CONTACTED: 'primary',
  IN_PROGRESS: 'warning',
  QUOTED: 'accent',
  WON: 'success',
  LOST: 'danger',
  ARCHIVED: 'neutral',
};

export function StatusBadge({ status }: { status: InquiryStatus }) {
  return <Badge tone={STATUS_TONE[status]}>{STATUS_LABEL[status]}</Badge>;
}

export function Pagination({ page, totalPages, onChange }: { page: number; totalPages: number; onChange: (page: number) => void }) {
  if (totalPages <= 1) return null;
  return (
    <nav aria-label="Pagination" className="flex items-center justify-between gap-3 text-[13px]">
      <button
        type="button"
        onClick={() => onChange(page - 1)}
        disabled={page <= 1}
        className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 font-semibold disabled:opacity-40"
      >
        <ChevronLeft className="h-4 w-4" aria-hidden /> Previous
      </button>
      <span className="text-muted" aria-current="page">
        Page {page} of {totalPages}
      </span>
      <button
        type="button"
        onClick={() => onChange(page + 1)}
        disabled={page >= totalPages}
        className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 font-semibold disabled:opacity-40"
      >
        Next <ChevronRight className="h-4 w-4" aria-hidden />
      </button>
    </nav>
  );
}

export function SectionHeading({
  eyebrow,
  title,
  intro,
  icon,
  className,
  as: As = 'h2',
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  intro?: ReactNode;
  icon?: ReactNode;
  className?: string;
  as?: 'h1' | 'h2';
}) {
  return (
    <div className={className}>
      {eyebrow && (
        <div className="inline-flex items-center gap-2">
          {icon}
          <span className="eyebrow text-muted">{eyebrow}</span>
        </div>
      )}
      <As className="mt-3 text-[30px] leading-[1.02] font-bold tracking-[-0.03em] lg:text-[40px]">{title}</As>
      {intro && <p className="mt-3 max-w-[560px] text-[14.5px] leading-relaxed text-muted">{intro}</p>}
    </div>
  );
}
